import * as React from 'react';
import { useImmerState } from '~/src/redesign/shared/service/hooks/use-immer-state';

interface GlobalState {
  viewedPricingMessage?: boolean;
  viewedShiftModal?: boolean;
  shiftPreferencesLastView?: boolean;
  lastVisitedPage?: string;
  viewedNotification?: boolean;
  filters?: Record<string, unknown>;

  userToken?: string;
  impersonateId?: string;
  impersonateUser?: string;
  qaAuthData?: string;
}

const sync: {
  context: {
    state: GlobalState;
    set: (partial: Partial<GlobalState>) => void;
    clear: (...fields: (keyof GlobalState)[]) => void;
  };
} = {
  context: {
    state: {},
    set: () => {},
    clear: () => {},
  },
};

const globalStateContext = React.createContext(sync.context);

export function parseLocalStorage(): GlobalState {
  try {
    return JSON.parse(localStorage.getItem('VP__state') as string) ?? {};
  } catch (error) {
    return {};
  }
}

export function persistLocalStorage(state: Partial<GlobalState>) {
  localStorage.setItem(
    'VP__state',
    JSON.stringify({ ...parseLocalStorage(), ...state }),
  );
}

export function useGlobalState() {
  return React.useContext(globalStateContext);
}

export function GlobalStateProvider(props: React.PropsWithChildren) {
  const [state, produceState] = useImmerState(parseLocalStorage);

  const clearState = React.useCallback((...fields: (keyof GlobalState)[]) => {
    produceState((draft) => {
      for (const field of fields) {
        delete draft[field];
      }

      localStorage.setItem('VP__state', JSON.stringify(draft));
      sync.context.state = parseLocalStorage();
    });
  }, []);

  const setState = React.useCallback((data: Partial<GlobalState>) => {
    produceState((draft) => {
      Object.assign(draft, data);
      localStorage.setItem('VP__state', JSON.stringify(draft));
      sync.context.state = parseLocalStorage();
    });
  }, []);

  const contextValue = React.useMemo(
    () => ({ state: state, set: setState, clear: clearState }),
    [state, setState, clearState],
  );

  React.useMemo(() => {
    sync.context.state = state;
    sync.context.set = setState;
    sync.context.clear = clearState;
  }, []);

  return <globalStateContext.Provider {...props} value={contextValue} />;
}

GlobalStateProvider.current = () => sync.context;
